<template>
  <form class="page-color-picker" @submit.prevent="confirmColor">
    <div class="form-group">
      <div class="color-default clearfix" v-if="customColors.length">
        <div
          class="color-button"
          :class="{
            'color-button--outline': c === '#ffffff',
          }"
          v-for="c in customColors"
          :key="c"
          :title="c"
          :style="`background: ${c};`"
          @click="setColor(c)"
        ></div>
      </div>
      <hr class="mt-2" v-if="customColors.length" />
      <div class="color-default clearfix">
        <div
          class="color-button"
          :class="{
            'color-button--outline': c === '#ffffff',
          }"
          v-for="c in colors"
          :key="c"
          :title="c"
          :style="`background: ${c};`"
          @click="setColor(c)"
        ></div>
        <div
          title="Clear"
          class="color-button color-button--clear color-button--outline"
          @click="setColor('')"
        ></div>
      </div>
    </div>
    <div class="form-group">
      <div class="d-flex justify-content-between">
        <label> {{ $t('GENERAL.LABEL.OPACITY') }}: </label>
        {{ opacityTextFormatter(opacity) }}
      </div>
      <HMSlider
        v-model="opacity"
        :min="0"
        :max="100"
        :interval="1"
        :tooltip-formatter="opacityTextFormatter"
      ></HMSlider>
    </div>
    <HMTextInput
      :value="textInputColor"
      @input="onColorTextInput"
      class="input-color-2"
    >
      <template slot="prepend">
        <div
          class="input-color"
          :style="previewColor"
          @click="selectHsl()"
        ></div>
      </template>
    </HMTextInput>
    <HMModalFooter
      :submitButton="$t('GENERAL.BUTTON.SELECT')"
      @close="close"
    ></HMModalFooter>
  </form>
</template>

<script>
import PageColorPickerHSL from './PageColorPickerHSL.vue';
import tinycolor from 'tinycolor2';

export default {
  name: 'PageColorPickerModal',
  props: {
    inputColor: {
      type: String,
    },
    customColors: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      color: '',
      opacity: 100,
      textInputColor: '',
      colors: [
        '#58362F',
        '#BF581E',
        '#EF6C00',
        '#FFBE93',
        '#FFE601',
        '#9E9D24',
        '#283593',
        '#D0E7E4',
        '#216C3B',
        '#BE95D7',
        '#667D89',
        '#000000',
        '#ffffff',
      ],
      renderOn: '',
    };
  },
  computed: {
    baseRGB() {
      const base = this.color;
      let rgb = null;
      if (base.indexOf('rgb') !== -1) {
        //rgb
        rgb = this.parseColor(base);
      }
      if (base.indexOf('#') !== -1) {
        //hex
        rgb = this.hexToRgb(base);
      }
      return rgb;
    },
    rgb() {
      return this.baseRGB;
    },
    hsl() {
      if (!this.rgb) return null;
      return this.rgbToHsl(this.rgb.r, this.rgb.g, this.rgb.b);
    },
    hue() {
      if (!this.rgb) return 0;
      return this.rgbToHue(this.rgb.r, this.rgb.g, this.rgb.b);
    },
    computedColor() {
      // Preview
      const transp = this.opacity / 100;
      let val = '';
      if (this.rgb) {
        if (transp * 1 === 1) {
          val = 'rgb(' + this.rgb.r + ',' + this.rgb.g + ',' + this.rgb.b + ')';
        } else {
          val =
            'rgba(' +
            this.rgb.r +
            ',' +
            this.rgb.g +
            ',' +
            this.rgb.b +
            ',' +
            transp +
            ')';
        }
      }
      return val;
    },
    gradientBackground() {
      if (!this.rgb) return '';
      return (
        'background-image: linear-gradient(90deg, rgba(' +
        this.rgb.r +
        ',' +
        this.rgb.g +
        ',' +
        this.rgb.b +
        ',0), rgba(' +
        this.rgb.r +
        ',' +
        this.rgb.g +
        ',' +
        this.rgb.b +
        ',1));'
      );
    },
    previewColor() {
      if (this.computedColor) {
        return `backgroundColor: ${this.computedColor};`;
      } else {
        return `background: none;`;
      }
    },
  },
  methods: {
    selectHsl() {
      this.$modal.show(
        PageColorPickerHSL,
        {
          inputColor: this.computedColor,
        },
        {
          classes: ['vm--color-picker-modal-hsl'],
        },
        {
          'color-selected': color => {
            this.setColor(color);
          },
        }
      );
    },
    confirmColor() {
      this.$parent.$emit('color-selected', this.computedColor);
      this.$emit('close');
    },
    close() {
      this.$emit('close');
    },
    parseInputColor(color) {
      const inputTinyColor = tinycolor(color);
      return inputTinyColor.isValid() ? inputTinyColor.toRgbString() : false;
    },
    onColorTextInput(value) {
      this.textInputColor = value;
      const inputColor = this.parseInputColor(value);
      if (inputColor) {
        this.opacity = 100;
        this.setColor(inputColor, false);
      }
    },
    _hex2dec(v) {
      return parseInt(v, 16);
    },
    _splitHEX(hex) {
      let c;
      if (hex.length === 4) {
        c = hex.replace('#', '').split('');
        return {
          r: this._hex2dec(c[0] + c[0]),
          g: this._hex2dec(c[1] + c[1]),
          b: this._hex2dec(c[2] + c[2]),
        };
      } else {
        return {
          r: this._hex2dec(hex.slice(1, 3)),
          g: this._hex2dec(hex.slice(3, 5)),
          b: this._hex2dec(hex.slice(5)),
        };
      }
    },
    _splitRGB(rgb) {
      let c = rgb.slice(rgb.indexOf('(') + 1, rgb.indexOf(')')).split(',');
      let flag = false;
      c = c.map(function (n, i) {
        // return (i !== 3) ? parseInt(n, 10) : flag = true, parseFloat(n);
        if (i !== 3) {
          return parseInt(n, 10);
        } else {
          flag = true;
          return parseFloat(n);
        }
      });
      const obj = {
        r: c[0],
        g: c[1],
        b: c[2],
      };
      if (flag) obj.a = c[3];
      return obj;
    },
    parseColor(col) {
      const slc = col.slice(0, 1);
      if (slc === '#') {
        return this._splitHEX(col);
      } else if (slc.toLowerCase() === 'r') {
        console.log('this._splitRGB(col)', this._splitRGB(col));
        return this._splitRGB(col);
      } else {
        console.log(
          '!Ooops! RGBvalues.color(' +
            col +
            ') : HEX, RGB, or RGBa strings only'
        );
      }
      return null;
    },
    rgbToHsl(r, g, b) {
      //r /= 255, g /= 255, b /= 255;
      r = r / 255;
      g = g / 255;
      b = b / 255;
      const max = Math.max(r, g, b),
        min = Math.min(r, g, b);
      let h, s;
      const l = (max + min) / 2;
      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
          default:
            break;
        }
        h /= 6;
      }
      return [(h * 100 + 0.5) | 0, (s * 100 + 0.5) | 0, (l * 100 + 0.5) | 0];
    },
    rgbToHue(r, g, b) {
      r /= 255;
      g /= 255;
      b /= 255;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const c = max - min;
      let hue;
      let segment;
      let shift;
      if (c === 0) {
        hue = 0;
      } else {
        switch (max) {
          case r:
            segment = (g - b) / c;
            shift = 0 / 60; // R° / (360° / hex sides)
            if (segment < 0) {
              // hue > 180, full rotation
              shift = 360 / 60; // R° / (360° / hex sides)
            }
            hue = segment + shift;
            break;
          case g:
            segment = (b - r) / c;
            shift = 120 / 60; // G° / (360° / hex sides)
            hue = segment + shift;
            break;
          case b:
            segment = (r - g) / c;
            shift = 240 / 60; // B° / (360° / hex sides)
            hue = segment + shift;
            break;
          default:
            break;
        }
      }
      return hue * 60; // hue is in [0,6], scale it up
    },
    hueToRgb(m1, m2, h) {
      if (h < 0) h = h + 1;
      else if (h > 1) h = h - 1;
      if (h * 6 < 1) return m1 + (m2 - m1) * h * 6;
      else if (h * 2 < 1) return m2;
      else if (h * 3 < 2) return m1 + (m2 - m1) * (2 / 3 - h) * 6;
      else return m1;
    },
    hslToRgb(h, s, l) {
      h /= 360;
      s /= 100;
      l /= 100;
      const m2 = l <= 0.5 ? l * (s + 1) : l + s - l * s,
        m1 = l * 2 - m2,
        r = parseInt(this.hueToRgb(m1, m2, h + 1 / 3) * 255),
        g = parseInt(this.hueToRgb(m1, m2, h) * 255),
        b = parseInt(this.hueToRgb(m1, m2, h - 1 / 3) * 255);
      return [r, g, b];
    },
    hexToRgb(hex) {
      if (hex === '') return null;
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : null;
    },
    hex(c) {
      const s = '0123456789abcdef';
      let i = parseInt(c);
      if (i === 0 || isNaN(c)) return '00';
      i = Math.round(Math.min(Math.max(0, i), 255));
      return s.charAt((i - (i % 16)) / 16) + s.charAt(i % 16);
    },
    rgbToHex(rgb) {
      return this.hex(rgb[0]) + this.hex(rgb[1]) + this.hex(rgb[2]);
    },
    generateHslaColors(saturation, lightness, alpha, amount) {
      const colors = [];
      const huedelta = Math.trunc(360 / amount);
      for (let i = 0; i < amount; i++) {
        const hue = i * huedelta;
        colors.push(`hsla(${hue},${lightness}%,${saturation}%,${alpha})`);
      }
      return colors;
    },
    setColor(color, setTextInput = true) {
      this.color = color;
      if (this.rgb && (this.rgb.a || this.rgb.a === 0)) {
        this.opacity = this.rgb.a * 100;
      }
      if (setTextInput) this.textInputColor = this.computedColor;
    },
    opacityTextFormatter(opacity) {
      return `${parseInt(opacity)}%`;
    },
  },
  mounted() {
    if (this.inputColor) {
      this.setColor(this.inputColor);
      if (this.rgb && (this.rgb.a || this.rgb.a === 0)) {
        this.opacity = this.rgb.a * 100;
      } else {
        this.opacity = 100;
      }
    }
  },
  watch: {
    computedColor(val) {
      // this.$parent.$emit('color-selected', this.computedColor);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.page-color-picker {
  .vm--modal {
    max-width: 271px;
    padding: 12px 12px;
  }

  button {
    box-shadow: 0px 3px 6px -6px rgb(0 0 0 / 32%);
    border: none;
    outline: none;
  }
}

.dot-1 {
  background: #f0f0f0;
  width: 7px;
  height: 8px;
}

.dot-2 {
  background: #fff;
  width: 7px;
  height: 8px;
}

.dot-3 {
  background: #f0f0f0;
  width: 7px;
  height: 7px;
}

.dot-4 {
  background: #fff;
  width: 7px;
  height: 7px;
}

.color-default {
  .color-button {
    display: inline-block;
    width: 42px;
    height: 42px;
    border-radius: 50% !important;
    margin: 2px;
    cursor: pointer;
    &--clear {
      background-image: url('/assets/images/opacity-icon.png');
    }
    &--outline {
      outline: #e3e7eb 1px solid;
      outline-offset: -1px;
    }
  }
}

.black-color-button {
  background: #111;
  color: #f3f3f3;
  border: transparent 1px solid;
  width: 35px;
  height: 35px;
  line-height: 35px;
  font-size: 10px;
  border-right: none;
}

.white-color-button {
  background: #fff;
  color: #111;
  width: 35px;
  height: 35px;
  line-height: 35px;
  font-size: 10px;
}

.clear-color-button {
  width: 132px;
  height: 35px;
  line-height: 35px;
  border-right: none;
  font-size: 10px;
  margin-left: 4px;
}

.transparency-input-group {
  display: flex;
  border: 1px solid $medium-gray;
  overflow: hidden;
  border-radius: 6px;
}

.input-color {
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: 40px;
  height: 40px;
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
  width: 40px;
  box-sizing: border-box;
  position: relative;
  cursor: pointer;
  &::before {
    content: '';
    width: 100%;
    height: 100%;
    background-size: cover;
    background-image: url('/assets/images/opacity-icon.png');
    position: absolute;
    z-index: -1;
    left: 0;
  }
}

.input-color-2 ::v-deep {
  .input-group-text {
    padding: 0;
  }
}

.input-text {
  flex: 1;
  width: 100%;
  border-left: 1px solid $medium-gray;
  border-right: 1px solid $medium-gray;
  border-top: none;
  border-bottom: none;
  padding: 0.5rem 0.75rem;
}

.input-hsl {
  width: 35px;
  height: 35px;
  line-height: 35px;
  font-size: 10px;
  margin-left: 4px;
}

.input-ok {
  width: 100%;
  height: 42px;
  width: 42px;
  background: transparent;
}
</style>
